/* eslint-disable import/prefer-default-export */
import type {
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import type {
  CreateUpdateStripeIntegrationProps,
  StripeIntegration,
} from 'types/stripe-integration'

import { QUERIES } from '@/enums/queries'

import { updateStripeIntegration } from './services/mutateStripeIntegration'
import { invalidateLoadStripeIntegrations } from './useLoadStripeIntegration'

type Props = {
  updateStripeIntegration: (
    id: string,
    data: CreateUpdateStripeIntegrationProps
  ) => Promise<StripeIntegration>
  isLoading: boolean
} & UseMutationResult<StripeIntegration, unknown, UpdateProps, unknown>

type UpdateProps = { id: string; data: CreateUpdateStripeIntegrationProps }

export const useUpdateStripeIntegrations = (
  options?: Omit<
    UseMutationOptions<StripeIntegration, Error, UpdateProps, unknown>,
    'mutationKey' | 'mutationFn' | 'onSuccess'
  >
): Props => {
  const mutation = useMutation({
    mutationKey: [QUERIES.INTEGRATIONS.STRIPE.UPDATE],
    mutationFn: async ({ id, data }: UpdateProps) =>
      await updateStripeIntegration(id, data),
    onSuccess: async () => {
      await invalidateLoadStripeIntegrations()
    },
    ...options,
  })

  const stripeIntegrationReq = async (
    id: string,
    data: CreateUpdateStripeIntegrationProps
  ) => {
    return mutation.mutateAsync({ id, data })
  }

  return {
    updateStripeIntegration: stripeIntegrationReq,
    ...mutation,
  }
}
